<template>
    <search-form-layout :popup="popup" :title="productName" @close-popup="$emit('close-popup')">
        <template v-slot:form>
            <v-form ref="form" v-model="valid">
                <v-row>
                    <v-col md="4" cols="12">
                        <country-autocomplete
                            v-model="location"
                            :rules="[v => !!v || `${$t('destination')} ${$t('validation.required')}`]"
                            label="destination"
                            single-line
                            filled
                        />
                    </v-col>
                    <v-col md="4" cols="12">
                        <daterange-m
                            :_start="startDate"
                            :_end="endDate"
                            @change-start="startDate = $event"
                            @change-end="endDate = $event"
                        />
                    </v-col>
                    <v-col md="2" cols="12">
                        <tourists-select-menu
                            :_adults="adults"
                            :_children-ages="childrenAges"
                            token="tourist"
                            @change-adults="adults = $event"
                            @change-children-ages="childrenAges = $event"
                        />
                    </v-col>
                    <v-col v-if="!$breakpoint.smAndDown" md="2" cols="12">
                        <v-btn block size="x-large" color="primary" :disabled="!valid" @click="search">
                            {{ $t('search') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-form>
            <additional-options-wrapper
                ref="addForm"
                :suppliers="suppliers"
                :options="['visaEntryType', 'internalSuppliers', 'externalSuppliers', 'supplierId']"
            />
            <v-row class="hidden-md-and-up">
                <v-col md="2" cols="12">
                    <v-btn block size="x-large" color="primary" :disabled="!valid" @click="search">
                        {{ $t('search') }}
                    </v-btn>
                </v-col>
            </v-row>
        </template>
    </search-form-layout>
</template>

<script lang="ts">
    import {Component, mixins, Prop} from 'vue-facing-decorator'
    import SearchFormLayout from '~/components/parts/SearchFormLayout.vue'
    import AdditionalOptionsWrapper from '~/components/snippets/AdditionalSearchOptions/additionalOptionsWrapper.src'
    import {CHANGE_LOCALE_EVENT, CHANGE_TAB_MOBILE_EVENT, EventBus} from '~/utils/event-bus'
    import CountryAutocomplete from '~/components/search/forms/CountryAutocomplete.vue'
    import DaterangeM from '~/components/search/forms/DaterangeM.vue'
    import DaysSelect from '~/components/snippets/AdditionalSearchOptions/DaysSelect.vue'
    import TouristsSelectMenu from '~/components/search/forms/TouristsSelectMenu.vue'
    import {AdditionalSearchOptionsMixin} from '~/mixins/AdditionalSearchOptionsMixin.vue'

    @Component({
        components: {
            SearchFormLayout,
            AdditionalOptionsWrapper,
            CountryAutocomplete,
            DaterangeM,
            DaysSelect,
            TouristsSelectMenu,
        },
    })
    export class VisaSearchForm extends mixins(AdditionalSearchOptionsMixin) {
        @Prop({default: false}) popup
        @Prop() productName

        valid = false
        location = null
        startDate = null
        endDate = null
        adults = 1
        childrenAges = []
        suppliers = []

        async mounted() {
            EventBus.$on(CHANGE_LOCALE_EVENT, this.reload)
            await this.load()
        }

        beforeUnmount() {
            EventBus.$off(CHANGE_LOCALE_EVENT, this.reload)
        }

        async load() {
            await this.$store.restored
            await this.$nextTick()
            const countryId = this.location?.id || this.searchRequest.countryId
            if (countryId && visaRuntimeStore.location.id !== countryId) {
                await visaRuntimeStore.loadLocation(countryId)
            }
            this.location =
                countryId && Object.keys(visaRuntimeStore.location).length ? visaRuntimeStore.location : null
            if (this.$dateFns.isAfter(this.$dateFns.parseISO(this.searchRequest.startDate), new Date())) {
                this.startDate = this.searchRequest.startDate
                this.endDate = this.searchRequest.endDate
            } else {
                this.startDate = this.$dateFns.format(this.$dateFns.addDays(new Date(), 1))
                this.endDate = this.$dateFns.format(this.$dateFns.addDays(new Date(), 10))
            }
            this.adults = authStore.user.isEmployeeOrExternalCorp ? 1 : this.searchRequest.adults
            this.childrenAges = authStore.user.isEmployeeOrExternalCorp ? [] : [...this.searchRequest.childrenAges]
            if (this.isTO1) {
                try {
                    this.suppliers = (await this.$api.suppliers.get({productType: 'ownVisa'}))?.suppliers
                    // eslint-disable-next-line no-empty
                } catch (e) {}
            }
        }

        async reload() {
            if (this.location && this.location.id) {
                try {
                    const {
                        countries: [country],
                    } = await this.$api.locations.get(this.location.id)
                    this.location = country
                    // eslint-disable-next-line no-empty
                } catch (e) {}
            }
        }

        async search() {
            this.$emit('close-popup')
            const query = this.newQuery()
            visaRuntimeStore.SET_DESTINATION_COUNTRY(this.location)
            const name = 'visaSearch'
            if (this.$route.name !== name) {
                visaRuntimeStore.newSearch()
            }
            try {
                await this.$router.push({name, query})
            } catch (e) {
                await visaRuntimeStore.search(visaStore.searchRequest)
            }
            EventBus.$emit(CHANGE_TAB_MOBILE_EVENT, 1)
        }

        newQuery() {
            const query = {
                countryId: this.location.id,
                startDate: this.startDate,
                endDate: this.endDate,
                adults: this.adults,
                childrenAges: this.childrenAges,
            }

            if (persistentStore.selectedCompanyAndAgent) {
                query.clientId = persistentStore.selectedCompanyAndAgent.company.id

                if (persistentStore.selectedCompanyAndAgent.agent) {
                    query.agentId = persistentStore.selectedCompanyAndAgent.agent.personId
                }
            }

            return query
        }

        get isTO1() {
            return authStore.isTO1
        }

        get searchRequest() {
            return visaStore.searchRequest
        }
    }

    export default VisaSearchForm
</script>
